/**
 * tkPeople.js
 * API for Te Kohinga people — reusable internal client records that are not
 * (yet) linked to a Paua person/client.
 */

import axiosInstance from "./axios-instance";

/**
 * List/search Te Kohinga people.
 * @param {{ q?: string, limit?: number, offset?: number }} params
 */
const listPeople = (params = {}) =>
    axiosInstance.get("/app/tk-people", { params, cache: false }).then(res => res);

/**
 * Retrieve a single Te Kohinga person by ID.
 */
const getPerson = (id) =>
    axiosInstance.get(`/app/tk-people/${id}`, { cache: false }).then(res => res);

/**
 * Create a new Te Kohinga person. `origin_service` is required by the server.
 */
const createPerson = (person) =>
    axiosInstance.post("/app/tk-people", person).then(res => res);

/**
 * Partially update an existing Te Kohinga person.
 */
const updatePerson = (id, person) =>
    axiosInstance.patch(`/app/tk-people/${id}`, person).then(res => res);

/**
 * Link a Te Kohinga person to a Paua person and/or client record.
 */
const linkPaua = (id, { paua_person_id = null, paua_client_id = null } = {}) =>
    axiosInstance.post(`/app/tk-people/${id}/link-paua`, { paua_person_id, paua_client_id }).then(res => res);

/**
 * List service engagements across all people, with person demographics joined.
 * @param {{ service?: string, services?: string, status?: string, order?: string, limit?: number, offset?: number }} params
 */
const listServiceEngagements = (params = {}) =>
    axiosInstance.get("/app/tk-engagements", { params, cache: false }).then(res => res);

/**
 * List service engagements for one Te Kohinga person.
 */
const listEngagements = (personId, params) =>
    axiosInstance.get(`/app/tk-people/${personId}/engagements`, { params: params || {}, cache: false }).then(res => res);

/**
 * Fetch the engagement lookup catalogues (services, statuses, exit codes, exit referrals).
 */
const getEngagementLookups = () =>
    axiosInstance.get("/app/tk-engagements/lookups", { cache: false }).then(res => res);

/**
 * Retrieve one service engagement with linked person profile fields.
 */
const getEngagement = (personId, engagementId) =>
    axiosInstance.get(`/app/tk-people/${personId}/engagements/${engagementId}`, { cache: false }).then(res => res);

/**
 * Retrieve service-applicable custom fields and existing engagement responses.
 */
const getEngagementCustomFields = (personId, engagementId) =>
    axiosInstance
        .get(`/app/tk-people/${personId}/engagements/${engagementId}/custom-fields`, { cache: false })
        .then(res => res);

/**
 * Create or update custom-field responses for a service engagement.
 */
const updateEngagementCustomFields = (personId, engagementId, responses) =>
    axiosInstance
        .patch(`/app/tk-people/${personId}/engagements/${engagementId}/custom-fields`, { responses })
        .then(res => res);

/**
 * Create a service engagement for a person. `service_code` is required.
 */
const createEngagement = (personId, engagement) =>
    axiosInstance.post(`/app/tk-people/${personId}/engagements`, engagement).then(res => res);

/**
 * Partially update a service engagement (lifecycle moves forward only:
 * Pre-Engagement (`waitlist`) -> active -> exited).
 */
const updateEngagement = (personId, engagementId, engagement) =>
    axiosInstance.patch(`/app/tk-people/${personId}/engagements/${engagementId}`, engagement).then(res => res);

/**
 * Mark a checklist item complete or incomplete for a service engagement.
 */
const updateChecklistItemCompletion = (personId, engagementId, checklistItemId, isCompleted) =>
    axiosInstance
        .patch(`/app/tk-people/${personId}/engagements/${engagementId}/checklist-items/${checklistItemId}`, { is_completed: isCompleted })
        .then(res => res);

/**
 * List case notes attached to a service engagement.
 */
const listCaseNotes = (personId, engagementId) =>
    axiosInstance.get(`/app/tk-people/${personId}/engagements/${engagementId}/case-notes`, { cache: false }).then(res => res);

/**
 * Create a case note attached to a service engagement.
 */
const createCaseNote = (personId, engagementId, note) =>
    axiosInstance.post(`/app/tk-people/${personId}/engagements/${engagementId}/case-notes`, note).then(res => res);

/**
 * Update an individual case note attached to a service engagement.
 */
const updateCaseNote = (personId, engagementId, caseNoteId, note) =>
    axiosInstance
        .patch(`/app/tk-people/${personId}/engagements/${engagementId}/case-notes/${caseNoteId}`, note)
        .then(res => res);

/**
 * Retrieve the current staff member's private draft and visible published insights.
 */
const getEngagementInsights = (personId, engagementId) =>
    axiosInstance
        .get(`/app/tk-people/${personId}/engagements/${engagementId}/insights`, { cache: false })
        .then(res => res);

/**
 * Generate a private engagement insight draft from eligible case notes.
 */
const generateEngagementInsight = (personId, engagementId) =>
    axiosInstance
        .post(`/app/tk-people/${personId}/engagements/${engagementId}/insights/generate`, null, { timeout: 300000 })
        .then(res => res);

/**
 * Save edits to a private engagement insight draft.
 */
const updateEngagementInsight = (personId, engagementId, insightId, content) =>
    axiosInstance
        .patch(`/app/tk-people/${personId}/engagements/${engagementId}/insights/${insightId}`, { content })
        .then(res => res);

/**
 * Publish an engagement insight draft.
 */
const publishEngagementInsight = (personId, engagementId, insightId) =>
    axiosInstance
        .post(`/app/tk-people/${personId}/engagements/${engagementId}/insights/${insightId}/publish`)
        .then(res => res);

/**
 * Download an immutable published insight as a Word document.
 */
const downloadEngagementInsight = (personId, engagementId, insightId) =>
    axiosInstance.get(`/app/tk-people/${personId}/engagements/${engagementId}/insights/${insightId}/export`, {
        cache: false,
        responseType: "blob",
    }).then(res => res);

export default {
    listPeople,
    getPerson,
    createPerson,
    updatePerson,
    linkPaua,
    listServiceEngagements,
    listEngagements,
    getEngagementLookups,
    getEngagement,
    getEngagementCustomFields,
    updateEngagementCustomFields,
    createEngagement,
    updateEngagement,
    updateChecklistItemCompletion,
    listCaseNotes,
    createCaseNote,
    updateCaseNote,
    getEngagementInsights,
    generateEngagementInsight,
    updateEngagementInsight,
    publishEngagementInsight,
    downloadEngagementInsight,
};
